// notifySendGift.js
'use strict';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import getGiftData from '../selector/getGiftData.js';
import getToyCommandData from '../selector/getToyCommandData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getIsKaraoke from '../selector/getIsKaraoke.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import sendLiveStreamGift from '../action/sendLiveStreamGift.js';
import fetchResource from '../action/fetchResource.js';
import { LIVESTREAM_ANIMATION_PAG_GIFTS } from '../RemoteConfigKeys.js';
import { livestream as livestreamDebug } from '../resource/debug.js';
import getMeData from '../selector/getMeData.js';
import login from '../action/login.js';
import toggleModal from '../action/toggleModal.js';

const log = livestreamDebug.extend('log:notifySendGift');

/**
 * notify users of sending animation gift
 * @kind action
 * @param {string} {streamId} - stream id
 * @param {string} {giftId} - gift id
 * @param {boolean} {isTooltip} - whether the gift is sent from tooltip
 * @param {'send'|'preload'} {mode} - send: HEAD + preload + POST (default). preload: HEAD + preload only (no POST).
 * @return {Promise} Action promise.
 */
const notifySendGift =
  ({ streamId, giftId, isTooltip = false, mode = 'send' }) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    if (!token) {
      dispatch(
        toggleModal({
          modalId: 'LivestreamGiftSendConfirmModal',
          isOpen: false,
        })
      );
      return dispatch(login({ isRequireDiamond: true }));
    }
    if (mode === 'send') {
      log(`gift [${giftId}] send triggered by user`, { streamId, giftId });
    }

    // Manually set status to `sending` here, and the second `sendLiveStreamGift()` will update status to success or error.
    // Skip setting status if gift is sent from tooltip
    // Also skip setting status if we are only preloading (user hasn't confirmed yet).
    if (!isTooltip && mode === 'send') {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['liveStream', 'gift'],
          data: {
            giftSendingStatus: 'sending',
          },
        },
      });
    }
    dispatch(
      sendLiveStreamGift({
        streamId,
        giftId,
        isHeadMethod: true,
        isTooltip,
      })
    );

    const isKaraoke = getIsKaraoke(getState(), streamId, giftId);
    const pagGiftList =
      getRemoteConfigData(getState(), LIVESTREAM_ANIMATION_PAG_GIFTS) || [];
    const isPagGift = pagGiftList.includes(giftId);
    let giftImage;

    if (isKaraoke) {
      const preset = getLiveStreamData(
        getState(),
        [streamId, 'karaoke', giftId, 'device'],
        'preset'
      );
      giftImage = getToyCommandData(getState(), preset, 'largeImage');
    } else if (isPagGift) {
      giftImage = getGiftData(getState(), giftId, 'pagImage');
    } else {
      giftImage = getGiftData(getState(), giftId, 'largeImage');
    }

    const assetType = isPagGift ? 'PAG' : 'APNG';
    log(`gift [${giftId}] preloading animation (${assetType})`, {
      giftId,
      assetType,
    });
    await dispatch(fetchResource(giftImage));
    log(`gift [${giftId}] animation preloaded`, { giftId });

    // Preload mode stops here: do NOT POST send gift.
    if (mode === 'preload') {
      return dispatch({ type: '' });
    }

    return dispatch(
      sendLiveStreamGift({
        streamId,
        giftId,
        isTooltip,
      })
    );
  };

export default notifySendGift;
